<template>
  <mia-panel
    title="Suchergebnis"
    class="rounded-t"
    :is-collapsed="isCollapsed"
    :without-title="inline"
    :seamless="inline"
  >
    <template #title="{ title }">
      <div class="flex items-center justify-between px-4 py-3 -mx-4 -my-3">
        <span
          class="inline-block w-full cursor-pointer"
          @click="isCollapsed = !isCollapsed"
          v-text="title"
        />

        <mia-collapsible-toggle-button
          :collapsed="isCollapsed"
          class="focus:outline-none"
          @click="isCollapsed = !isCollapsed"
        />
      </div>
    </template>

    <div class="flex items-center justify-between px-4 py-2">
      <div class="flex items-center">
        <mia-column-filter
          v-model="visibleColumns"
          :config="columnConfig"
          @reset="resetVisibleColumns"
        />

        <mia-feature-input
          v-model="query"
          class="ml-3"
          placeholder="Tabelle durchsuchen..."
          @input="onQueryChanged"
        />

        <mia-submit-button
          :disabled="$wait.any"
          :is-loading="$wait.is('exporting')"
          label="Excel Export"
          class="ml-3 export-button"
          type="button"
          @click="$emit('export')"
        />
      </div>

      <mia-page-size-select
        v-model="perPage"
        @input="onPerPageChanged"
      />
    </div>

    <div class="overflow-x-auto overflow-y-hidden">
      <datatable
        ref="datatable"
        name="searchResultsTable"
        :columns="activeColumns"
        :data="cars"
        :filter="query"
        :per-page="perPage"
        class="sticky w-full table-auto datatable"
        v-on="{
          'vue-datatable::pager-bound': onPagerBound,
          'vue-datatable::page-changed': onPageChanged,
          'vue-datatable::page-count-changed': onPageCountChanged,
        }"
      >
        <template #no-results>
          Keine Ergebnisse
        </template>
      </datatable>
    </div>

    <template #controls>
      <div class="flex items-center justify-between flex-1">
        <mia-datatable-results-counter
          :page="page"
          :per-page="perPage"
          :total="total"
          class="flex items-center"
        />

        <mia-datatable-pager
          ref="pager"
          v-model="page"
          table="searchResultsTable"
          class="ml-8"
          type="abbreviated"
        />
      </div>
    </template>
  </mia-panel>
</template>

<script>
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import GrossNetPriceColumn from '@/components/cars/search/datatable/GrossNetPriceColumn.vue'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import PowerColumn from '@/components/cars/search/datatable/PowerColumn.vue'
import FlagsColumn from '@/components/cars/search/datatable/FlagsColumn.vue'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import CarStatusColumn from '@/components/ui/datatable/columns/CarStatusColumn.vue'
import CarVtiStatusColumn from '@/components/ui/datatable/columns/CarVtiStatusColumn.vue'
import DateTimeColumn from '@/components/ui/datatable/columns/DateTimeColumn.vue'
import LinkColumn from '@/components/ui/datatable/columns/LinkColumn.vue'
import ColumnFilter from '@/components/ui/datatable/ColumnFilter.vue'
import PageSizeSelect from '@/components/ui/datatable/PageSizeSelect.vue'
import PriceColumn from '@/components/ui/datatable/columns/PriceColumn.vue'
import CollapsibleToggleButton from '@/components/ui/CollapsibleToggleButton.vue'
import Panel from '@/components/ui/widgets/Panel.vue'
import { isEmpty, fullName, handleEmptyDate } from '@/helpers/utils'

export default {
  name: 'MiaSearchResults',

  components: {
    'mia-collapsible-toggle-button': CollapsibleToggleButton,
    'mia-column-filter': ColumnFilter,
    'mia-feature-input': FeatureInput,
    'mia-panel': Panel,
    'mia-page-size-select': PageSizeSelect,
    'mia-submit-button': SubmitButton
  },

  props: {
    columnConfig: {
      type: Array,
      required: true
    },

    inline: {
      type: Boolean,
      default: false
    },

    actions: {
      type: Object,
      default() {
        return null
      }
    }
  },

  data() {
    return {
      total: 0,
      isCollapsed: true,
      restorePageWith: null,
      columns: {
        id: {
          label: 'Fahrzeug-ID',
          field: 'id',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id',
            before: (car) => {
              this.$store.commit('search/setLastSearchCarId', car.id)
            }
          }
        },
        created: {
          label: 'Angelegt',
          field: 'created',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        fin: {
          label: 'FIN',
          field: 'fin',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id',
            before: (car) => {
              this.$store.commit('search/setLastSearchCarId', car.id)
            }
          }
        },
        commission_number: {
          label: 'Kommissionsnummer',
          field: 'commission_number',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id'
          }
        },
        shared: {
          label: 'Freigegeben',
          field: 'shared',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            schema: '/cars/:id/boersendaten'
          }
        },
        dealerName: {
          label: 'Händler',
          field: 'dealerName',
          headerAlign: 'left'
        },
        blocked_end: {
          label: 'Reserviert bis',
          field: 'blockedEnd',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy HH:mm:ss \'Uhr\'')
            }
          }
        },
        shared_title: {
          label: 'Titel',
          field: 'shared_title',
          headerAlign: 'left'
        },
        stock: {
          label: 'Lagerplatz',
          field: 'stockSlotId',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            labelProp: (row) => {
              if (isEmpty(row.stockSlotId)) return ''

              return row.stockSlotTitle
            },
            schema: (row) => {
              if (isEmpty(row.stockSlotId)) return ''

              return '/legacy/stock_edit/:stockSlotId'
            }
          }
        },
        manufacturer_color: {
          label: 'Farbe',
          field: 'manufacturer_color',
          headerAlign: 'left',
          representedAs: row => (isEmpty(row.manufacturer_color) || row.manufacturer_color === 'null' ? '' : row.manufacturer_color)
        },
        net_list_price: {
          label: 'UPE Netto (ohne Garantie für Prämienberechnung)',
          field: 'net_list_price',
          headerAlign: 'left',
          align: 'right',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        net_list_price_at_ordertime: {
          label: 'UPE zum Bestellzeitpunkt Netto',
          field: 'net_list_price_at_ordertime',
          headerAlign: 'left',
          align: 'right',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        net_list_price_invoice: {
          label: 'UPE aus Rechnung Netto',
          field: 'net_list_price_invoice',
          headerAlign: 'left',
          align: 'right',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        price_customer: {
          label: 'Angebotspreis Endkunde',
          field: 'price_customer',
          headerAlign: 'left',
          align: 'right',
          component: GrossNetPriceColumn,
          meta: {
            isDifferentialTaxed: row => row.tax === 'differential',
            pricePurchasedField: 'price_purchased'
          }
        },
        price_partner: {
          label: 'Angebotspreis Vermittler',
          field: 'price_partner',
          headerAlign: 'left',
          align: 'right',
          component: GrossNetPriceColumn,
          meta: {
            isDifferentialTaxed: row => row.tax === 'differential',
            pricePurchasedField: 'price_purchased'
          }
        },
        price_retailer: {
          label: 'Angebotspreis Wiederverkäufer',
          field: 'price_retailer',
          headerAlign: 'left',
          align: 'right',
          component: GrossNetPriceColumn,
          meta: {
            isDifferentialTaxed: row => row.tax === 'differential',
            pricePurchasedField: 'price_purchased'
          }
        },
        seller: {
          label: 'Verkäufer',
          field: 'sellerId',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            labelProp: (row) => {
              if (isEmpty(row.sellerId)) return ''

              return fullName(row.sellerLastname, row.sellerFirstname)
            },
            schema: (row) => {
              if (isEmpty(row.sellerId)) return ''

              return '/user/profile/:sellerId'
            }
          }
        },
        make: {
          label: 'Marke',
          field: 'make',
          headerAlign: 'left'
        },
        model: {
          label: 'Modell',
          field: 'model',
          headerAlign: 'left'
        },
        model_range: {
          label: 'Baureihe',
          field: 'model_range',
          headerAlign: 'left'
        },
        trimline: {
          label: 'Ausstattungslinie',
          field: 'trimline',
          headerAlign: 'left'
        },
        category: {
          label: 'Kategorie',
          field: 'category',
          headerAlign: 'left',
          representedAs: row => this.getCategoryLabel(row.category)
        },
        type: {
          label: 'Typ',
          field: 'type',
          headerAlign: 'left',
          representedAs: row => this.getTypeLabel(row.type)
        },
        model_code: {
          label: 'Modellcode',
          field: 'model_code',
          headerAlign: 'left'
        },
        powerkw: {
          label: 'Leistung',
          field: 'powerkw',
          headerAlign: 'left',
          align: 'right',
          component: PowerColumn
        },
        fuel: {
          label: 'Kraftstoff',
          field: 'fuel',
          headerAlign: 'left',
          representedAs: row => this.getFuelLabel(row.fuel)
        },
        gearbox_type: {
          label: 'Getriebe',
          field: 'gearbox_type',
          headerAlign: 'left',
          representedAs: row => this.getGearboxLabel(row.gearbox_type)
        },
        km: {
          label: 'Kilometerstand',
          field: 'km',
          headerAlign: 'left',
          representedAs: (row) => {
            if (isEmpty(row.km)) return ''

            return Intl.NumberFormat('de-DE').format(row.km)
          }
        },
        status: {
          label: 'Status',
          field: 'status',
          headerAlign: 'left',
          component: CarStatusColumn,
          meta: {
            statusOptions: () => this.statusOptions
          }
        },
        vti_status: {
          label: 'VTI Status',
          field: 'vti_status',
          headerAlign: 'left',
          component: CarVtiStatusColumn
        },
        customer: {
          label: 'Kunde',
          field: 'customerId',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            labelProp: (row) => {
              if (isEmpty(row.customerId)) return ''

              return fullName(row.customerName, row.customerFirstname)
            },
            schema: (row) => {
              if (isEmpty(row.customerId)) return ''

              return '/legacy/customer_edit/:customerId'
            }
          }
        },
        assignee: {
          label: 'Zuständiger',
          field: 'assigneeId',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            labelProp: (row) => {
              if (isEmpty(row.assigneeId)) return ''

              return fullName(row.assigneeLastname, row.assigneeFirstname)
            },
            schema: (row) => {
              if (isEmpty(row.assigneeId)) return ''

              return '/user/profile/:assigneeId'
            }
          }
        },
        expected_dispatch: {
          label: 'Erwartete Auslieferung',
          field: 'expected_dispatch',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        dispatch: {
          label: 'Terminierte Auslieferung',
          field: 'dispatch',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        certificate: {
          label: 'Brieflagerplatz',
          field: 'certificateSlotId',
          headerAlign: 'left',
          component: LinkColumn,
          meta: {
            labelProp: (row) => {
              if (isEmpty(row.certificateSlotId)) return ''

              return row.certificateSlotTitle
            },
            schema: (row) => {
              if (isEmpty(row.certificateSlotId)) return ''

              return '/legacy/certificate_edit/1:certificateSlotId'
            }
          }
        },
        numberplate: {
          label: 'Kennzeichen',
          field: 'numberplate',
          headerAlign: 'left'
        },
        initialregistration: {
          label: 'Erstzulassung',
          field: 'initialregistration',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        initialregistration_on_customer: {
          label: 'Erstzulassung auf Kunden',
          field: 'initialregistration_on_customer',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        deregistration: {
          label: 'Abmeldung',
          field: 'deregistration',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        sold_date: {
          label: 'Datum Kaufvertrag',
          field: 'sold_date',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        invoicedate_sold: {
          label: 'Rechnungsdatum',
          field: 'invoicedate_sold',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        invoicedate_purchased: {
          label: 'EK Rechnungsdatum',
          field: 'invoicedate_purchased',
          headerAlign: 'left',
          component: DateTimeColumn,
          meta: {
            parseDate: (dateStr) => {
              if (isEmpty(handleEmptyDate(dateStr))) return null

              return DateTime.fromSQL(dateStr)
            },
            formatDate: (date) => {
              if (date === null) return ''

              return date.toFormat('dd.MM.yyyy')
            }
          }
        },
        wintertyres: {
          label: 'Winterreifen',
          field: 'wintertyres',
          headerAlign: 'left',
          representedAs: row => (row.wintertyres === 'Y' ? 'Ja' : 'Nein')
        },
        notes: {
          label: 'Anmerkungen',
          field: 'notes',
          headerAlign: 'left'
        },
        provision_requested: {
          label: 'Prämien angefordert',
          field: 'provision_requested',
          headerAlign: 'left',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        provision_paid: {
          label: 'Prämien bezahlt',
          field: 'provision_paid',
          headerAlign: 'left',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        revenue: {
          label: 'Ertrag',
          field: 'revenue',
          headerAlign: 'left',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        financial_values: {
          label: 'Finanzierungsertrag',
          field: 'financial_values',
          headerAlign: 'left',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        accessories_revenue: {
          label: 'Zubehörertrag',
          field: 'accessories_revenue',
          headerAlign: 'left',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        sold_channel: {
          label: 'Vertriebskanal',
          field: 'salesChannelTitle',
          headerAlign: 'left'
        },
        flags: {
          label: 'Flags',
          field: 'flags',
          headerAlign: 'left',
          component: FlagsColumn
        },
        actions: this.actions || {
          label: 'Aktion',
          field: '',
          sortable: false,
          headerAlign: 'left',
          align: 'right',
          component: ActionButtonColumn,
          meta: {
            actions: [
              {
                icon: 'pencil-write',
                attrs: {
                  title: 'Bearbeiten'
                },
                onClick: (car) => {
                  this.$store.commit('search/setLastSearchCarId', car.id)
                  this.$router.push(`/cars/${car.id}`)
                }
              },
              {
                icon: 'bin-1',
                class: 'border-red-600 text-red-600 hover:bg-red-600',
                attrs: {
                  title: 'Löschen'
                },
                isVisible: (car) => {
                  const dealerOwnsCar = parseInt(car.dealer, 10) === parseInt(this.authDealer.id, 10)
                  const isRootOrAdmin = ['root', 'admin'].includes(this.authUser.item_group)
                  const hasPermission = this.authUser.item_is_allowed_trash_cars

                  return dealerOwnsCar && (isRootOrAdmin || hasPermission)
                },
                onClick: (car) => {
                  mia.car.remove.confirm(car.id, car.fin)
                }
              }
            ]
          }
        }
      }
    }
  },

  computed: {
    ...mapGetters([
      'mobileOptions',
      'mobileCategories',
      'carTypes',
      'statusOptions'
    ]),
    ...mapGetters('search', [
      'cars',
      'lastSearch'
    ]),

    visibleColumns: {
      get() {
        return this.$store.state.search.visibleColumns
      },

      set(visibleColumns) {
        this.$store.dispatch('search/updateVisibleColumns', visibleColumns)
      }
    },

    activeColumns() {
      return Object.entries(this.columns)
        .filter(([column]) => this.visibleColumns.includes(column))
        .map(([, config]) => config)
    },

    query: {
      get() {
        return this.$store.state.search.lastSearch.query
      },

      set(query) {
        this.$store.commit('search/setLastSearchQuery', query)
      }
    },

    page: {
      get() {
        return this.$store.state.search.lastSearch.page
      },

      set(page) {
        this.$store.commit('search/setLastSearchPage', parseInt(page, 10))
      }
    },

    perPage: {
      get() {
        return this.$store.state.search.lastSearch.perPage
      },

      set(perPage) {
        this.$store.commit('search/setLastSearchPerPage', parseInt(perPage, 10))
      }
    }
  },

  created() {
    this.restoreVisibleColumnsFromPreferences()

    if (this.visibleColumns.length === 0) {
      this.resetVisibleColumns()
    }

    if (!this.visibleColumns.includes('actions')) {
      this.visibleColumns = [...this.visibleColumns, 'actions']
    }

    this.$store.dispatch('loadMobileOptions')
    this.$store.dispatch('loadMobileCategories')
    this.$store.dispatch('loadCarTypes')
    this.$store.dispatch('loadStatusOptions')

    if (this.lastSearch.page > 1) {
      this.restorePageWith = this.lastSearch.page
    }
  },

  methods: {
    restoreVisibleColumnsFromPreferences() {
      if (!this.authUser.preferences || !this.authUser.preferences.item_car_search_visible_columns) {
        return
      }

      this.$store.commit('search/setVisibleColumns', this.authUser.preferences.item_car_search_visible_columns)
    },

    resetVisibleColumns() {
      this.visibleColumns = this.columnConfig
        .filter(({ isDefault }) => isDefault)
        .map(({ column }) => column)
    },

    getCategoryLabel(category) {
      const mobileCategory = this.mobileCategories.find(({ id }) => id === category)

      return mobileCategory ? mobileCategory.title : ''
    },

    getTypeLabel(type) {
      const carType = this.carTypes.find(({ id }) => id === type)

      return carType ? carType.label : ''
    },

    getFuelLabel(fuel) {
      const fuelType = this.mobileOptions.fuelTypes.find(({ name }) => name === fuel)

      return fuelType ? fuelType.description : ''
    },

    getGearboxLabel(gearboxType) {
      const mobileGearboxType = this.mobileOptions.gearboxTypes.find(({ name }) => name === gearboxType)

      return mobileGearboxType ? mobileGearboxType.description : ''
    },

    show() {
      this.isCollapsed = false
    },

    restorePagerPage(page) {
      this.$nextTick(() => {
        this.$refs.pager.navigate(page)
      })
    },

    onPageChanged(page) {
      this.page = page
    },

    onPageCountChanged() {
      this.total = this.$refs.datatable.totalRows
    },

    onPagerBound() {
      // This is the "ready" event of the pager component
      this.$nextTick(() => {
        if (this.restorePageWith) {
          this.restorePagerPage(this.restorePageWith)
          this.restorePageWith = null
        }
      })
    },

    onQueryChanged() {
      this.resetPage()
    },

    onPerPageChanged() {
      this.resetPage()
    },

    resetPage() {
      if (this.page !== 1) {
        this.$nextTick(() => {
          this.$refs.datatable.page = 1
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.export-button {
  @apply bg-transparent;
  @apply py-1;
  @apply text-green-500;
  @apply border-green-500;
  @apply items-center;

  height: 35px;

  &:hover {
    @apply bg-green-500;
    @apply text-white;
  }
}
</style>
